import React from 'react';
import styled from 'styled-components';
import { appConfig } from 'config/app';
import { lineHeight, theme, toRem } from 'design';
import { ConnectIntegrationType } from 'hooks/useConnectIntegration';
import RocketImage from 'images/illustrations/RocketLaunch.svg';
import Image from 'images/illustrations/Wallet.svg';
import { toTitleCase } from 'utils/strings';
import { ButtonStyles, IButtonProps } from '../Button';
import { Icon } from '../Icon';
import { H1, Text } from '../Typography';

const Layout = styled.div`
  display: flex;
  flex-flow: column nowrap;
  flex-grow: 1;
  justify-content: center;
  align-items: center;
  width: 100%;
  background: ${theme.color.neutral[800]};
`;

const Content = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: ${toRem(theme.space['3xl'])};
  justify-items: center;
  width: 100%;

  img {
    width: 100%;
    max-width: ${toRem(500)};
  }
`;

const Messaging = styled.div`
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  max-width: ${toRem(500)};
  text-align: center;

  ${H1} {
    color: ${theme.color.white};

    @media (min-width: ${theme.breakpoint.large}px) {
      font-size: ${toRem(theme.font.size['5xl'])};
      line-height: ${lineHeight(theme.font.size['5xl'])};
    }
  }

  ${Text} {
    margin-top: ${toRem(theme.space.md)};
    color: ${theme.color.white};
  }

  button,
  a {
    margin-top: ${toRem(theme.space.xl)};
  }
`;

const Link = styled.a<Partial<IButtonProps>>`
  fill: ${theme.color.secondary[300]};
  ${ButtonStyles}

  &:hover, &:focus {
    fill: ${theme.color.neutral[900]};
  }
`;

const Overline = styled(Text)`
  color: ${theme.color.secondary[300]}!important;
  text-transform: uppercase;
  margin-bottom: ${toRem(theme.space.sm)};
`;

const StyledIcon = styled(Icon)`
  rotate: 180deg;
  margin-left: ${toRem(theme.space.sm)};
`;

interface AuthMarketingItemProps {
  ctaLabel: string;
  ctaLink: string;
  image: string;
  message: string;
  title: string;
  overline?: string;
  ctaAppearance?: 'tertiary' | 'transparent';
  ctaIcon?: boolean;
}

const AuthMarketingItem = ({ ctaLabel, ctaLink, image, message, title, overline, ctaAppearance = 'tertiary', ctaIcon }: AuthMarketingItemProps) => (
  <Layout>
    <Content>
      <img alt="Money Transfer" src={image} />

      <Messaging>
        {overline && (
          <Overline size="default" fontWeight="semibold">
            {overline}
          </Overline>
        )}
        <H1>{title}</H1>
        <Text size="default">{message}</Text>

        <Link appearance={ctaAppearance} href={ctaLink} target="_blank" rel="noopener noreferrer" size="large">
          {ctaLabel}
          {ctaIcon && <StyledIcon width={16} height={16} icon="ArrowLeft" />}
        </Link>
      </Messaging>
    </Content>
  </Layout>
);

const AuthMarketing = () => (
  <AuthMarketingItem
    ctaLabel="Make A Referral"
    ctaLink={appConfig.LINKS.KICKFIN_REFERRALS}
    image={Image}
    message="Know a restaurant or bar that needs Kickfin? Make an intro! You’ll get a generous bonus for every location that starts using Kickfin because of your referral."
    title="Make a referral. Make money."
  />
);

export const AuthIntegrationMarketing = ({ integration }: { integration: ConnectIntegrationType }) => (
  <AuthMarketingItem
    ctaLabel="Get a Demo"
    ctaLink={appConfig.LINKS.KICKFIN_DEMO}
    image={RocketImage}
    message="If you are not yet a Kickfin customer contact us to learn more about our product and to create an account"
    title={`Automate tip calculations with Kickfin & ${toTitleCase(integration)}`}
    overline="New to Kickfin?"
    ctaAppearance="transparent"
    ctaIcon
  />
);

export default AuthMarketing;
